import { useCallback, useEffect, useMemo, useState } from "react";
import { SpaceIcon, ProcurementPackageIcon, AlertIcon, WorkSectionIcon, PlotIcon, TaskListIcon } from "@/lib/icons";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { formatResponsible } from "@/lib/responsibleUser";
import {
  ColumnLayout,
  Row,
  SectionHeader,
  DetailPanel,
  DetailField,
  EmptyCol,
  ComboboxRow,
  useColumnState,
  type ComboboxOption,
  type ColumnDescriptor,
  type ColumnNavigation,
} from "@/components/columnView/ColumnLayout";
import type {
  PlotState,
  ProcurementPackageState,
  SchedulingSpace,
  TemplateTaskAvailabilityMaps,
} from "./hooks/useProjectInitData";
import type { UserOption } from "./hooks/useProjectInitialisationData";
import {
  FIELDS,
  WorkSectionSpaceTree,
  getCheckboxState,
  useFlatTreeModel,
  type SpaceUpdate,
} from "./WorkSectionSpaceTree";

const EMPTY_COLLAPSED: Set<string> = new Set();

/** Sentinel value used in the Tarkastaja combobox to represent
 *  "inherit from the procurement package's responsible user".  Stored in
 *  WorkSectionState as inspectorUserId = null; lifted to this string only at
 *  the ComboboxRow boundary so the option can be selected and displayed. */
const PP_RESPONSIBLE_SENTINEL = "__pp_responsible__";

const PP_CHECKBOX_TASK_NAMES = [
  "Suunnitelmiin perehtyminen",
  "Aliurakkasopimukseen perehtyminen",
  "Aloitusedellytykset",
  "Taloudellinen loppuselvitys",
];

// ─── TaskCheckboxRow ──────────────────────────────────────────────────────────

const TOOLTIP_UNAVAILABLE = "Tehtävä ei löydy valitusta tehtäväpohjasta";

function TaskCheckboxRow({
  checked,
  available,
  onChange,
  onSelect,
  label,
  isSelected,
  displayOnly = false,
}: {
  checked: boolean;
  available: boolean;
  onChange: (v: boolean) => void;
  onSelect?: () => void;
  label: string;
  isSelected?: boolean;
  displayOnly?: boolean;
}) {
  const { isActive } = useColumnState();
  const fullySelected = !!isSelected && isActive;
  function handleClick() {
    onSelect?.();
    if (available && !displayOnly) onChange(!checked);
  }
  const row = (
    <Row isSelected={isSelected} onClick={handleClick}>
      <div className={cn(
        "flex items-center gap-2",
        displayOnly ? "opacity-50" : (!available && "opacity-50"),
      )}>
        <Checkbox
          checked={displayOnly ? checked : (available && checked)}
          onCheckedChange={() => {}}
          className={cn(
            "pointer-events-none shrink-0",
            fullySelected && "border-primary-foreground data-[state=checked]:bg-primary-foreground data-[state=checked]:text-primary",
          )}
        />
        <span className="text-xs truncate">{label}</span>
      </div>
    </Row>
  );
  if (!available && !displayOnly) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div>{row}</div>
        </TooltipTrigger>
        <TooltipContent>{TOOLTIP_UNAVAILABLE}</TooltipContent>
      </Tooltip>
    );
  }
  return row;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function plotMissingCount(plot: PlotState): number {
  return plot.procurementPackages.filter(
    (pp) => !pp.responsibleUserId && !pp.isExisting,
  ).length;
}

function WarningBadge({
  count,
  isSelected,
}: {
  count: number | string;
  isSelected: boolean;
}) {
  const { isActive } = useColumnState();
  const selected = isSelected && isActive;
  const parentSelected = isSelected && !isActive;
  const className = selected
    ? "border-primary-foreground/50 text-primary-foreground/70 bg-transparent"
    : parentSelected
    ? "border-muted-foreground/40 text-muted-foreground bg-transparent"
    : "border-destructive/40 text-destructive bg-destructive/5";
  return (
    <Badge
      variant="outline"
      className={`text-[10px] px-1 py-0 leading-tight shrink-0 transition-none ${className}`}
    >
      {count}
    </Badge>
  );
}

function RowName({
  value,
  isSelected,
}: {
  value: string;
  isSelected: boolean;
}) {
  const { isActive } = useColumnState();
  const selected = isSelected && isActive;
  return (
    <div
      className={`text-xs truncate ${
        selected ? "text-primary-foreground" : "text-foreground"
      }`}
    >
      {value}
    </div>
  );
}

function RowCode({ code, isSelected }: { code: string; isSelected: boolean }) {
  const { isActive } = useColumnState();
  const selected = isSelected && isActive;
  return (
    <span
      className={`font-mono text-[11px] shrink-0 ${
        selected ? "text-primary-foreground/70" : "text-muted-foreground"
      }`}
    >
      {code}
    </span>
  );
}

// ─── Column 1: Plot list ──────────────────────────────────────────────────────

function PlotRows({
  plots,
  selectedIdx,
  onSelect,
}: {
  plots: PlotState[];
  selectedIdx: number | null;
  onSelect: (idx: number) => void;
}) {
  return (
    <>
      <SectionHeader
        icon={<PlotIcon className="h-3 w-3" />}
        label={`Tontit (${plots.length})`}
      />
      {plots.length === 0 && <EmptyCol label="Ei tontteja" />}
      {plots.map((plot, idx) => {
        const missingCount = plotMissingCount(plot);
        const isSel = idx === selectedIdx;
        return (
          <Row
            key={plot.locationSchedulingSpaceId ?? plot.name}
            isSelected={isSel}
            hasWarning={missingCount > 0 && !isSel}
            hasChildren
            onClick={() => onSelect(idx)}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 min-w-0">
                {missingCount > 0 && (
                  <WarningBadge count={missingCount} isSelected={isSel} />
                )}
              </div>
              <RowName value={plot.name || "—"} isSelected={isSel} />
            </div>
          </Row>
        );
      })}
    </>
  );
}

// ─── Column 2: Procurement package list ───────────────────────────────────────

function ProcurementPackageRows({
  plot,
  selectedIdx,
  onSelect,
}: {
  plot: PlotState;
  selectedIdx: number | null;
  onSelect: (idx: number) => void;
}) {
  return (
    <>
      <SectionHeader icon={<PlotIcon className="h-3 w-3" />} label="Tontti" />
      <DetailPanel>
        <DetailField label="Nimi" value={plot.name || "—"} />
      </DetailPanel>
      <SectionHeader
        icon={<ProcurementPackageIcon className="h-3 w-3" />}
        label={`Hankintapaketit (${plot.procurementPackages.length})`}
      />
      {plot.procurementPackages.length === 0 && (
        <EmptyCol label="Ei hankintapaketteja" />
      )}
      {plot.procurementPackages.map((pp, idx) => {
        const isMissingResponsible = !pp.responsibleUserId && !pp.isExisting;
        const isSel = idx === selectedIdx;
        return (
          <Row
            key={pp.templateProcurementPackageId}
            isSelected={isSel}
            hasWarning={isMissingResponsible && !isSel}
            hasChildren
            onClick={() => onSelect(idx)}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 min-w-0">
                <RowCode code={pp.code || "—"} isSelected={isSel} />
                {isMissingResponsible && (
                  <WarningBadge count="!" isSelected={isSel} />
                )}
                {pp.isExisting && (
                  <Badge
                    variant="outline"
                    className="text-[10px] px-1 py-0 leading-tight shrink-0 transition-none border-muted-foreground/30 text-muted-foreground bg-muted/30"
                  >
                    Jo olemassa
                  </Badge>
                )}
              </div>
              <RowName value={pp.name || "—"} isSelected={isSel} />
            </div>
          </Row>
        );
      })}
    </>
  );
}

// ─── Column 3: Procurement package content ────────────────────────────────────

function ProcurementPackageContent({
  pp,
  users,
  templateTaskAvailability,
  onUpdate,
  isRowSelected,
  isRowActivated,
  onRowClick,
  onRowDeactivate,
  selectedRowIndex,
  onSelectRow,
  selectedWorkSectionIdx,
  onSelectWorkSection,
  readOnly = false,
  taskCheckboxesReadOnly = false,
}: {
  pp: ProcurementPackageState;
  users: UserOption[];
  templateTaskAvailability: TemplateTaskAvailabilityMaps;
  onUpdate: (updated: ProcurementPackageState) => void;
  isRowSelected: boolean;
  isRowActivated: boolean;
  onRowClick: () => void;
  onRowDeactivate: () => void;
  selectedRowIndex: number | null;
  onSelectRow: (index: number) => void;
  selectedWorkSectionIdx: number | null;
  onSelectWorkSection: (idx: number) => void;
  readOnly?: boolean;
  taskCheckboxesReadOnly?: boolean;
}) {
  const userOptions: ComboboxOption[] = users.map((u) => ({
    value: u.id,
    label: `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email,
    description: u.email,
  }));

  const ppAvailable =
    pp.templateProcurementPackageId !== "unassigned"
      ? templateTaskAvailability.availableAtPp.get(
          pp.templateProcurementPackageId
        ) ?? new Set<string>()
      : new Set<string>();
  const canSuunnitelmiin = ppAvailable.has("Suunnitelmiin perehtyminen");
  const canAliurakka = ppAvailable.has("Aliurakkasopimukseen perehtyminen");
  const canMestanValmistelu = ppAvailable.has("Aloitusedellytykset");
  const canTaloudellinen = ppAvailable.has("Taloudellinen loppuselvitys");

  // PP-level controls become read-only when either the whole editor is
  // read-only or this specific PP is flagged as already-existing (mini-init).
  // Work sections under the PP remain editable in either case so newly-
  // discovered sections can still be configured.
  const ppReadOnly = readOnly || !!pp.isExisting;
  const ppTaskCheckboxesReadOnly = taskCheckboxesReadOnly || !!pp.isExisting;

  return (
    <>
      <SectionHeader icon={<ProcurementPackageIcon className="h-3 w-3" />} label="Hankintapaketti" />
      <DetailPanel>
        <DetailField label="Koodi – Nimi" value={<span>{pp.code || "—"} – {pp.name || "—"}</span>} />
      </DetailPanel>
      <SectionHeader icon={<TaskListIcon className="h-3 w-3" />} label="Tehtävät" />
      <ComboboxRow
        isSelected={isRowSelected}
        isActivated={isRowActivated}
        onDeactivate={onRowDeactivate}
        options={userOptions}
        value={pp.responsibleUserId}
        onChange={(val) => onUpdate({ ...pp, responsibleUserId: val })}
        placeholder="Valitse vastuuhenkilö *"
        searchPlaceholder="Hae käyttäjää..."
        onClick={onRowClick}
        disabled={ppReadOnly}
      >
        {!pp.responsibleUserId && !ppReadOnly && (
          <AlertIcon className="h-3.5 w-3.5 text-destructive shrink-0" />
        )}
        <span className="text-[10px] font-semibold opacity-60 uppercase tracking-wide shrink-0">
          Vastuuhenkilö
        </span>
      </ComboboxRow>
      <TooltipProvider delayDuration={150}>
        <TaskCheckboxRow
          isSelected={selectedRowIndex === 1}
          onSelect={() => onSelectRow(1)}
          checked={pp.includeSuunnitelmiin}
          available={canSuunnitelmiin}
          onChange={(v) => onUpdate({ ...pp, includeSuunnitelmiin: v })}
          label="Suunnitelmiin perehtyminen"
          displayOnly={ppTaskCheckboxesReadOnly}
        />
        <TaskCheckboxRow
          isSelected={selectedRowIndex === 2}
          onSelect={() => onSelectRow(2)}
          checked={pp.includeAliurakka}
          available={canAliurakka}
          onChange={(v) => onUpdate({ ...pp, includeAliurakka: v })}
          label="Aliurakkasopimukseen perehtyminen"
          displayOnly={ppTaskCheckboxesReadOnly}
        />
        <TaskCheckboxRow
          isSelected={selectedRowIndex === 3}
          onSelect={() => onSelectRow(3)}
          checked={pp.includeAloitusedellytykset}
          available={canMestanValmistelu}
          onChange={(v) => onUpdate({ ...pp, includeAloitusedellytykset: v })}
          label="Aloitusedellytykset"
          displayOnly={ppTaskCheckboxesReadOnly}
        />
        <TaskCheckboxRow
          isSelected={selectedRowIndex === 4}
          onSelect={() => onSelectRow(4)}
          checked={pp.includeTaloudellinenLoppuselvitys}
          available={canTaloudellinen}
          onChange={(v) => onUpdate({ ...pp, includeTaloudellinenLoppuselvitys: v })}
          label="Taloudellinen loppuselvitys"
          displayOnly={ppTaskCheckboxesReadOnly}
        />
      </TooltipProvider>
      <SectionHeader
        icon={<WorkSectionIcon className="h-3 w-3" />}
        label={`Työvaiheet (${pp.workSections.length})`}
      />
      {pp.workSections.length === 0 && <EmptyCol label="Ei työvaiheita" />}
      {pp.workSections.map((ws, idx) => {
        const isSel = selectedWorkSectionIdx === idx;
        return (
          <Row
            key={`${ws.sectionCode}-${idx}`}
            isSelected={isSel}
            hasChildren
            onClick={() => onSelectWorkSection(idx)}
          >
            <div className="min-w-0">
              <RowCode code={ws.sectionCode} isSelected={isSel} />
              <RowName value={ws.sectionName || ws.sectionCode} isSelected={isSel} />
            </div>
          </Row>
        );
      })}
    </>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

interface ProjectColumnEditorProps {
  plots: PlotState[];
  users: UserOption[];
  allSchedulingSpaces: SchedulingSpace[];
  templateTaskAvailability: TemplateTaskAvailabilityMaps;
  onUpdatePlot: (plotIndex: number, updated: PlotState) => void;
  spaceIdToLocationId: Map<string, string>;
  readOnly?: boolean;
  taskCheckboxesReadOnly?: boolean;
  focusTrigger?: number;
}

export function ProjectColumnEditor({
  plots,
  users,
  allSchedulingSpaces,
  templateTaskAvailability,
  onUpdatePlot,
  spaceIdToLocationId,
  readOnly = false,
  taskCheckboxesReadOnly = false,
  focusTrigger,
}: ProjectColumnEditorProps) {
  const [selectedPlotIdx, setSelectedPlotIdx] = useState<number | null>(null);
  const [selectedPPIdx, setSelectedPPIdx] = useState<number | null>(null);
  const [selectedPpContentIdx, setSelectedPpContentIdx] = useState<number | null>(null);
  const [isPpContentActivated, setIsPpContentActivated] = useState(false);
  // Column 4 keyboard navigation state. Row 0 is the Tarkastaja combobox
  // (cellIdx 0 only); rows 1..N are the flat tree rows in render order.
  const [selectedWsRowIdx, setSelectedWsRowIdx] = useState<number | null>(null);
  const [selectedWsCellIdx, setSelectedWsCellIdx] = useState<number>(0);
  const [isInspectorActivated, setIsInspectorActivated] = useState(false);
  // Collapsed-spaces state, persisted per work section across switches.
  const [collapsedByWs, setCollapsedByWs] = useState<Map<string, Set<string>>>(
    () => new Map(),
  );

  useEffect(() => {
    if (selectedPlotIdx !== null && selectedPlotIdx >= plots.length) {
      setSelectedPlotIdx(plots.length > 0 ? 0 : null);
      setSelectedPPIdx(null);
    }
  }, [plots.length, selectedPlotIdx]);

  // Reset column 3 state whenever the selected PP changes.
  useEffect(() => {
    setSelectedPpContentIdx(null);
    setIsPpContentActivated(false);
  }, [selectedPPIdx]);

  const selectedPlot =
    selectedPlotIdx !== null ? (plots[selectedPlotIdx] ?? null) : null;

  const selectedPP =
    selectedPlot !== null && selectedPPIdx !== null
      ? (selectedPlot.procurementPackages[selectedPPIdx] ?? null)
      : null;

  // Derive work section selection from the unified column-3 row index (>= 5).
  const selectedWorkSectionIdx =
    selectedPpContentIdx !== null && selectedPpContentIdx >= 5
      ? selectedPpContentIdx - 5
      : null;

  function selectPlot(idx: number) {
    setSelectedPlotIdx(idx);
    setSelectedPPIdx(null);
  }

  const plotNav: ColumnNavigation = {
    length: plots.length,
    selectedIndex: selectedPlotIdx,
    onSelectIndex: (i) => selectPlot(i),
  };

  const ppNav: ColumnNavigation | undefined = selectedPlot
    ? {
        length: selectedPlot.procurementPackages.length,
        selectedIndex: selectedPPIdx,
        onSelectIndex: (i) => setSelectedPPIdx(i),
        onClear: () => setSelectedPPIdx(null),
      }
    : undefined;

  type PpBoolField =
    | "includeSuunnitelmiin"
    | "includeAliurakka"
    | "includeAloitusedellytykset"
    | "includeTaloudellinenLoppuselvitys";

  function togglePpCheckbox(checkboxIdx: number) {
    if (!selectedPlot || selectedPPIdx === null) return;
    const pp = selectedPlot.procurementPackages[selectedPPIdx];
    if (!pp) return;
    const ppAvail =
      pp.templateProcurementPackageId !== "unassigned"
        ? templateTaskAvailability.availableAtPp.get(pp.templateProcurementPackageId) ?? new Set<string>()
        : new Set<string>();
    const fields: Array<[string, PpBoolField]> = [
      ["Suunnitelmiin perehtyminen", "includeSuunnitelmiin"],
      ["Aliurakkasopimukseen perehtyminen", "includeAliurakka"],
      ["Aloitusedellytykset", "includeAloitusedellytykset"],
      ["Taloudellinen loppuselvitys", "includeTaloudellinenLoppuselvitys"],
    ];
    const entry = fields[checkboxIdx];
    if (!entry || !ppAvail.has(entry[0])) return;
    onUpdatePlot(selectedPlotIdx!, {
      ...selectedPlot,
      procurementPackages: selectedPlot.procurementPackages.map((p, i) =>
        i === selectedPPIdx ? { ...p, [entry[1]]: !p[entry[1]] } : p
      ),
    });
  }

  const ppAvailForNav =
    selectedPP && selectedPP.templateProcurementPackageId !== "unassigned"
      ? (templateTaskAvailability.availableAtPp.get(selectedPP.templateProcurementPackageId) ?? new Set<string>())
      : new Set<string>();

  const isPpContentRowEnabled = (i: number): boolean => {
    if (i < 1 || i > 4) return true;
    if (taskCheckboxesReadOnly) return true;
    return ppAvailForNav.has(PP_CHECKBOX_TASK_NAMES[i - 1]);
  };

  const ppContentNav: ColumnNavigation | undefined =
    selectedPPIdx !== null && selectedPlot !== null
      ? {
          length: 5 + (selectedPP?.workSections.length ?? 0),
          selectedIndex: selectedPpContentIdx,
          onSelectIndex: (i) => {
            if (!isPpContentRowEnabled(i)) {
              const dir = selectedPpContentIdx === null || i > selectedPpContentIdx ? 1 : -1;
              const len = 5 + (selectedPP?.workSections.length ?? 0);
              for (let next = i + dir; next >= 0 && next < len; next += dir) {
                if (isPpContentRowEnabled(next)) {
                  setSelectedPpContentIdx(next);
                  return;
                }
              }
              return;
            }
            setSelectedPpContentIdx(i);
          },
          onClear: () => setSelectedPpContentIdx(null),
          onActivate: readOnly
            ? undefined
            : (i) => {
                if (i === 0) setIsPpContentActivated(true);
                else if (i >= 1 && i <= 4 && !taskCheckboxesReadOnly) togglePpCheckbox(i - 1);
              },
        }
      : undefined;

  // ─── Column 4 (work-section tree) — flat model + cell navigation ────────────

  const currentWs =
    selectedPP !== null && selectedWorkSectionIdx !== null
      ? selectedPP.workSections[selectedWorkSectionIdx] ?? null
      : null;

  const wsAvailable = useMemo(() => {
    if (taskCheckboxesReadOnly) {
      const spaces = currentWs?.spaces ?? [];
      const s = new Set<string>();
      if (spaces.some((sp) => sp.includeMalliasennustarkastus)) s.add("Malliasennustarkastus");
      if (spaces.some((sp) => sp.includeOsakohteenTarkastus)) s.add("Osakohteen tarkastus");
      if (spaces.some((sp) => sp.includeMestanVastaanotto)) s.add("Mestan vastaanotto");
      if (spaces.some((sp) => sp.includeTyovaiheenvastaanotto)) s.add("Työvaiheenvastaanotto");
      if (spaces.some((sp) => sp.includeLiitedokumentit)) s.add("Liitedokumentit");
      return s;
    }
    return currentWs?.templateWorkSectionId
      ? templateTaskAvailability.availableAtWs.get(currentWs.templateWorkSectionId) ?? new Set<string>()
      : new Set<string>();
  }, [taskCheckboxesReadOnly, currentWs?.spaces, currentWs?.templateWorkSectionId, templateTaskAvailability.availableAtWs]);

  const wsKey =
    selectedPlotIdx !== null &&
    selectedPPIdx !== null &&
    selectedWorkSectionIdx !== null
      ? `${selectedPlotIdx}:${selectedPPIdx}:${selectedWorkSectionIdx}`
      : null;

  const collapsedSpaces = wsKey
    ? collapsedByWs.get(wsKey) ?? EMPTY_COLLAPSED
    : EMPTY_COLLAPSED;

  const setCollapsedSpacesForCurrentWs = useCallback(
    (updater: (prev: Set<string>) => Set<string>) => {
      if (!wsKey) return;
      setCollapsedByWs((prev) => {
        const next = new Map(prev);
        next.set(wsKey, updater(prev.get(wsKey) ?? new Set()));
        return next;
      });
    },
    [wsKey],
  );

  const emptySpaces = useMemo(() => [], []);
  const wsSpaces = currentWs?.spaces ?? emptySpaces;

  const { flatRows, availability } = useFlatTreeModel({
    spaces: wsSpaces,
    allSchedulingSpaces,
    availableAtWs: wsAvailable,
    collapsedSpaces,
    plotName: selectedPlot?.name ?? "",
  });

  // Reset row/cell selection whenever the selected work section changes.
  useEffect(() => {
    setSelectedWsRowIdx(null);
    setSelectedWsCellIdx(0);
    setIsInspectorActivated(false);
  }, [selectedPlotIdx, selectedPPIdx, selectedWorkSectionIdx]);

  function isWsCellEnabled(row: number, cell: number): boolean {
    const fr = flatRows[row];
    if (!fr || fr.kind !== "space") return false;
    if (cell === 0) return true;
    const fieldIdx = cell - 1;
    if (fieldIdx < 0 || fieldIdx >= FIELDS.length) return false;
    const enabled = availability[FIELDS[fieldIdx].availabilityKey];
    return fr.node.descendantTaskSpaces.length > 0 && enabled;
  }

  function updateWsSpaces(updates: SpaceUpdate[]) {
    if (
      !selectedPlot ||
      selectedPlotIdx === null ||
      selectedPPIdx === null ||
      selectedWorkSectionIdx === null
    )
      return;
    const pp = selectedPlot.procurementPackages[selectedPPIdx];
    if (!pp) return;
    const ws = pp.workSections[selectedWorkSectionIdx];
    if (!ws) return;
    const updatedSpaces = ws.spaces.map((s) => {
      const u = updates.find((upd) => upd.spaceId === s.schedulingSpaceId);
      return u ? { ...s, [u.field]: u.value } : s;
    });
    onUpdatePlot(selectedPlotIdx, {
      ...selectedPlot,
      procurementPackages: selectedPlot.procurementPackages.map((p, i) =>
        i === selectedPPIdx
          ? {
              ...p,
              workSections: p.workSections.map((w, j) =>
                j === selectedWorkSectionIdx
                  ? { ...w, spaces: updatedSpaces }
                  : w,
              ),
            }
          : p,
      ),
    });
  }

  function activateWsCell(row: number, cellIdx?: number) {
    const cell = cellIdx ?? 0;
    const fr = flatRows[row];
    if (!fr || fr.kind !== "space") return;
    if (cell === 0) {
      if (fr.hasChildren) {
        const id = fr.node.space.id;
        setCollapsedSpacesForCurrentWs((prev) => {
          const next = new Set(prev);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return next;
        });
      }
      return;
    }
    if (!isWsCellEnabled(row, cell)) return;
    const fieldDesc = FIELDS[cell - 1];
    const state = getCheckboxState(
      fr.node.descendantTaskSpaces,
      wsSpaces,
      fieldDesc.field,
    );
    const target = state !== "checked";
    updateWsSpaces(
      fr.node.descendantTaskSpaces.map((id) => ({
        spaceId: id,
        field: fieldDesc.field,
        value: target,
      })),
    );
  }

  function setInspectorForCurrentWs(value: string | null) {
    if (
      !selectedPlot ||
      selectedPlotIdx === null ||
      selectedPPIdx === null ||
      selectedWorkSectionIdx === null
    )
      return;
    const resolved = value === PP_RESPONSIBLE_SENTINEL ? null : value;
    onUpdatePlot(selectedPlotIdx, {
      ...selectedPlot,
      procurementPackages: selectedPlot.procurementPackages.map((p, i) =>
        i === selectedPPIdx
          ? {
              ...p,
              workSections: p.workSections.map((w, j) =>
                j === selectedWorkSectionIdx
                  ? { ...w, inspectorUserId: resolved }
                  : w,
              ),
            }
          : p,
      ),
    });
  }

  // Column 4 navigation reserves row 0 for the Tarkastaja combobox and maps
  // rows 1..N to the tree's flatRows (offset by +1). isWsCellEnabled and
  // activateWsCell stay tree-local; the wrappers below do the offset.
  const wsContentNav: ColumnNavigation | undefined = currentWs
    ? {
        length: 1 + flatRows.length,
        selectedIndex: selectedWsRowIdx,
        onSelectIndex: (i) => setSelectedWsRowIdx(i),
        onClear: () => {
          setSelectedWsRowIdx(null);
          setSelectedWsCellIdx(0);
          setIsInspectorActivated(false);
        },
        onActivate: readOnly
          ? undefined
          : (i, cellIdx) => {
              if (i === 0) setIsInspectorActivated(true);
              else activateWsCell(i - 1, cellIdx);
            },
        cells: {
          selectedCellIndex: selectedWsCellIdx,
          onSelectCellIndex: (cell) => setSelectedWsCellIdx(cell),
          isCellEnabled: (row, cell) =>
            row === 0 ? cell === 0 : isWsCellEnabled(row - 1, cell),
          cellsPerRow: 6,
        },
      }
    : undefined;

  const ppContent = (() => {
    if (!selectedPlot || selectedPPIdx === null) {
      return <EmptyCol label="Valitse hankintapaketti" />;
    }
    const pp = selectedPlot.procurementPackages[selectedPPIdx];
    if (!pp) return <EmptyCol />;
    const plotIdx = selectedPlotIdx!;
    return (
      <ProcurementPackageContent
        pp={pp}
        users={users}
        templateTaskAvailability={templateTaskAvailability}
        isRowSelected={selectedPpContentIdx === 0}
        isRowActivated={isPpContentActivated}
        onRowClick={() => {
          setSelectedPpContentIdx(0);
          if (!readOnly) setIsPpContentActivated(true);
        }}
        onRowDeactivate={() => setIsPpContentActivated(false)}
        selectedRowIndex={selectedPpContentIdx}
        onSelectRow={(i) => setSelectedPpContentIdx(i)}
        selectedWorkSectionIdx={selectedWorkSectionIdx}
        onSelectWorkSection={(idx) => setSelectedPpContentIdx(5 + idx)}
        onUpdate={(updated) =>
          onUpdatePlot(plotIdx, {
            ...selectedPlot,
            procurementPackages: selectedPlot.procurementPackages.map((p, i) =>
              i === selectedPPIdx ? updated : p
            ),
          })
        }
        readOnly={readOnly}
        taskCheckboxesReadOnly={taskCheckboxesReadOnly}
      />
    );
  })();

  const wsContent = (() => {
    if (!currentWs) {
      return <EmptyCol label="Valitse työvaihe" />;
    }
    const ppRespUser = selectedPP?.responsibleUserId
      ? users.find((u) => u.id === selectedPP.responsibleUserId)
      : undefined;
    // Substitution-aware: the PP responsible shown here renders "B (A)" when
    // substituted. The user picker below stays plain (you select a person).
    const ppRespLabel = ppRespUser ? formatResponsible(ppRespUser) : undefined;
    const inspectorOptions: ComboboxOption[] = [
      {
        value: PP_RESPONSIBLE_SENTINEL,
        label: "Hankintapaketin vastuuhenkilö",
        description: ppRespLabel,
        triggerLabel: ppRespLabel
          ? `Hankintapaketin vastuuhenkilö (${ppRespLabel})`
          : undefined,
      },
      ...users.map((u) => ({
        value: u.id,
        label: `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email,
        description: u.email,
      })),
    ];
    const inspectorValue = currentWs.inspectorUserId ?? PP_RESPONSIBLE_SENTINEL;
    const treeFocusedRowIndex =
      selectedWsRowIdx !== null && selectedWsRowIdx >= 1
        ? selectedWsRowIdx - 1
        : null;

    const wsResponsibles = currentWs?.spaceResponsibles ?? {};

    // Effective per scheduling-space id: nearest ancestor (incl. self) with a
    // responsible set, walking the scheduling-space parent chain. `inherited`
    // is true when the match came from an ancestor, not the row itself.
    const spaceById = new Map(allSchedulingSpaces.map((s) => [s.id, s]));
    const ownerBySpaceId = new Map<string, string>();
    for (const [spaceId, entry] of Object.entries(wsResponsibles)) {
      if (entry.responsibleUserId) ownerBySpaceId.set(spaceId, entry.responsibleUserId);
    }
    const responsibleBySpaceId = new Map<string, { userId: string; inherited: boolean }>();
    const resolveEffective = (startId: string) => {
      let current: string | null | undefined = startId;
      let depth = 0;
      while (current && depth < 20) {
        const owner = ownerBySpaceId.get(current);
        if (owner) {
          responsibleBySpaceId.set(startId, {
            userId: owner,
            inherited: current !== startId,
          });
          return;
        }
        current = spaceById.get(current)?.parentId ?? null;
        depth += 1;
      }
    };
    for (const s of allSchedulingSpaces) resolveEffective(s.id);

    const setSpaceResponsible = (schedulingSpaceId: string, userId: string | null) => {
      if (
        !selectedPlot ||
        selectedPlotIdx === null ||
        selectedPPIdx === null ||
        selectedWorkSectionIdx === null
      )
        return;
      // In the project viewer this resolves to a real DB location id (used to
      // upsert the section-location on save). In init / mini-init the map is
      // empty — staging with null still drives the tree display, and the worker
      // persists the assignment server-side by scheduling_space_id.
      const locationId = spaceIdToLocationId.get(schedulingSpaceId) ?? null;
      onUpdatePlot(selectedPlotIdx, {
        ...selectedPlot,
        procurementPackages: selectedPlot.procurementPackages.map((p, i) =>
          i === selectedPPIdx
            ? {
                ...p,
                workSections: p.workSections.map((w, j) => {
                  if (j !== selectedWorkSectionIdx) return w;
                  const prev = w.spaceResponsibles[schedulingSpaceId];
                  return {
                    ...w,
                    spaceResponsibles: {
                      ...w.spaceResponsibles,
                      [schedulingSpaceId]: {
                        locationId,
                        responsibleUserId: userId,
                        sectionLocationId: prev?.sectionLocationId ?? null,
                        sectionLocationVersion: prev?.sectionLocationVersion ?? null,
                        originalResponsibleUserId:
                          prev?.originalResponsibleUserId ?? null,
                      },
                    },
                  };
                }),
              }
            : p,
        ),
      });
    };

    return (
      <>
        <SectionHeader icon={<WorkSectionIcon className="h-3 w-3" />} label="Työvaihe" />
        <DetailPanel>
          <DetailField label="Koodi – Nimi" value={<span>{currentWs.sectionCode || "—"} – {currentWs.sectionName || "—"}</span>} />
        </DetailPanel>
        <SectionHeader icon={<TaskListIcon className="h-3 w-3" />} label="Tehtävät" />
        <ComboboxRow
          isSelected={selectedWsRowIdx === 0}
          isActivated={isInspectorActivated}
          onDeactivate={() => setIsInspectorActivated(false)}
          options={inspectorOptions}
          value={inspectorValue}
          onChange={(val) => setInspectorForCurrentWs(val)}
          placeholder="Valitse tarkastaja"
          searchPlaceholder="Hae käyttäjää..."
          onClick={() => {
            setSelectedWsRowIdx(0);
            if (!readOnly) setIsInspectorActivated(true);
          }}
          disabled={readOnly}
        >
          <span className="text-[10px] font-semibold opacity-60 uppercase tracking-wide shrink-0">
            Tarkastaja
          </span>
        </ComboboxRow>
        <div className="flex-1 overflow-y-auto p-3">
          <WorkSectionSpaceTree
            spaces={currentWs.spaces}
            allSchedulingSpaces={allSchedulingSpaces}
            plotName={selectedPlot?.name ?? ""}
            availableAtWs={wsAvailable}
            onUpdateSpace={updateWsSpaces}
            collapsedSpaces={collapsedSpaces}
            onSetCollapsedSpaces={setCollapsedSpacesForCurrentWs}
            focusedRowIndex={treeFocusedRowIndex}
            focusedCellIndex={selectedWsCellIdx}
            onFocusCell={(row, cell) => {
              setSelectedWsRowIdx(row + 1);
              setSelectedWsCellIdx(cell);
            }}
            displayOnly={taskCheckboxesReadOnly}
            responsibleUsers={users}
            responsibleBySpaceId={responsibleBySpaceId}
            onSetSpaceResponsible={readOnly ? undefined : setSpaceResponsible}
          />
        </div>
      </>
    );
  })();

  const columns: ColumnDescriptor[] = [
    {
      key: "plots",
      width: "w-40",
      navigation: plotNav,
      content: (
        <PlotRows
          plots={plots}
          selectedIdx={selectedPlotIdx}
          onSelect={selectPlot}
        />
      ),
    },
    {
      key: "pps",
      width: "w-44",
      navigation: ppNav,
      content: selectedPlot ? (
        <ProcurementPackageRows
          plot={selectedPlot}
          selectedIdx={selectedPPIdx}
          onSelect={(idx) => {
            if (idx === selectedPPIdx && selectedPpContentIdx !== null) {
              setSelectedPpContentIdx(null);
              setIsPpContentActivated(false);
            } else {
              setSelectedPPIdx(idx);
            }
          }}
        />
      ) : (
        <EmptyCol />
      ),
    },
    {
      key: "pp-content",
      width: "w-64",
      navigation: ppContentNav,
      content: ppContent,
    },
    {
      key: "ws-content",
      width: "flex-1",
      navigation: wsContentNav,
      content: wsContent,
    },
  ];

  return (
    <ColumnLayout
      columns={columns}
      enableHorizontalScroll={false}
      className="h-full"
      focusTrigger={focusTrigger}
    />
  );
}
